/**
 * useInfiniteScroll — observa um elemento sentinela e chama `loadMore()`
 * quando ele entra no viewport. Anti-spam: bloqueia chamadas adicionais
 * enquanto a anterior nao terminou (controle externo via `loading`).
 *
 * Uso:
 *   const sentinelRef = useInfiniteScroll(loadMore, hasMore, loading);
 *   <div ref={sentinelRef} />
 */
import { useEffect, useRef } from 'react';

export function useInfiniteScroll(loadMore, hasMore, loading, rootMargin = '300px') {
  const sentinelRef = useRef(null);
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);

  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry.isIntersecting) return;
      if (loadingRef.current) return;
      if (!hasMoreRef.current) return;
      loadMore();
    }, { rootMargin });

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, hasMore, rootMargin]);

  return sentinelRef;
}
